import { Banner, Button, Link } from '@shopify/polaris';
import { WidgetsList } from '../components/widgets-list.js';
import { Queries } from '../query/query-client';
import { useQuery } from '@tanstack/react-query';
import { useWidgetStore, type WidgetConfig } from '../store/widget.store';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useMemo, type CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxesStacked, faChartSimple, faEye, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { WidgetType } from '../widgets/widgets';

const apiKey = document.querySelector('meta[name="shopify-api-key"]')?.getAttribute('content') || '';

const widgetTypeLabel: Record<WidgetType, string> = {
    [WidgetType.DEFAULT]: 'Default',
    [WidgetType.COUNTDOWN]: 'Countdown',
    [WidgetType.INVENTORY]: 'Inventory',
    [WidgetType.FREE_SHIPPING]: 'Free shipping',
};

export default function HomePage() {
    const navigate = useNavigate();

    const { resetState } = useWidgetStore(
        useShallow(state => ({
            resetState: state.resetState,
        }))
    );

    const { data: widgetsList } = useQuery<WidgetConfig[]>({
        queryKey: [Queries.WIDGET_GET_ALL],
    });

    const { data: shopInfo } = useQuery<{ embeddingEnabled: boolean; myshopifyDomain: string; appUUID: string | null }>(
        {
            queryKey: [Queries.SHOP_GET_INFO],
        }
    );

    const stats = useMemo(() => {
        const widgets = widgetsList ?? [];
        const totalWidgets = widgets.length;
        const activeWidgets = widgets.filter(widget => widget.active).length;
        const totalViews = widgets.reduce((sum, widget) => sum + (widget.views || 0), 0);

        const widgetByType = widgets.reduce(
            (acc, widget) => {
                const key = widget.widgetType;
                acc[key] = (acc[key] || 0) + 1;
                return acc;
            },
            {} as Partial<Record<WidgetType, number>>
        );

        const topTypeEntry = (Object.entries(widgetByType) as [WidgetType, number][]).sort((a, b) => b[1] - a[1])[0];
        const topTypeName = topTypeEntry ? widgetTypeLabel[topTypeEntry[0]] : 'No widgets yet';
        const topTypeCount = topTypeEntry ? String(topTypeEntry[1]) : '0';

        return [
            {
                key: 'total-widgets',
                label: 'Total widgets',
                value: String(totalWidgets),
                meta: `${activeWidgets} active`,
                icon: faBoxesStacked,
                tone: 'mv-stat-card--accent',
            },
            {
                key: 'active-ratio',
                label: 'Activation rate',
                value: totalWidgets ? `${Math.round((activeWidgets / totalWidgets) * 100)}%` : '0%',
                meta: `${activeWidgets}/${totalWidgets} currently live`,
                icon: faChartSimple,
                tone: 'mv-stat-card--secondary',
            },
            {
                key: 'total-views',
                label: 'Total views',
                value: totalViews.toLocaleString(),
                meta: 'Across all widget placements',
                icon: faEye,
                tone: 'mv-stat-card--sun',
            },
            {
                key: 'top-widget-type',
                label: 'Top widget type',
                value: topTypeName,
                meta: `${topTypeCount} widget${topTypeCount === '1' ? '' : 's'}`,
                icon: faLayerGroup,
                tone: 'mv-stat-card--ink',
            },
        ];
    }, [widgetsList]);

    return (
        <div className="mv-home-wrapper">
            {shopInfo?.myshopifyDomain && !shopInfo?.embeddingEnabled && (
                <div className="mv-embed-warning">
                    <Banner tone="critical" title="Enable app embedding to show widgets on your storefront.">
                        Open the{' '}
                        <Link
                            url={`https://${shopInfo.myshopifyDomain}/admin/themes/current/editor?context=apps&activateAppId=${apiKey}/timer_block`}
                        >
                            theme editor
                        </Link>{' '}
                        and click <b>Save</b> in the top-right corner to publish the app embed block.
                    </Banner>
                </div>
            )}

            <section className="mv-home-hero mv-reveal">
                <div>
                    <p className="mv-home-eyebrow">Dashboard</p>
                    <h2 className="mv-home-title">Performance Snapshot</h2>
                    <p className="mv-home-subtitle">
                        Keep your urgency widgets sharp, active, and aligned with how customers browse your store.
                    </p>
                </div>

                <Button
                    tone="success"
                    variant="primary"
                    size="large"
                    onClick={() => {
                        resetState();
                        navigate('/manage-widget');
                    }}
                >
                    Add new widget
                </Button>
            </section>

            <section className="mv-stats-grid">
                {stats.map((stat, index) => (
                    <article
                        key={stat.key}
                        className={`mv-stat-card ${stat.tone} mv-reveal`}
                        style={{ animationDelay: `${120 + index * 80}ms` } as CSSProperties}
                    >
                        <div className="mv-stat-head">
                            <p className="mv-stat-label">{stat.label}</p>
                            <span className="mv-stat-icon">
                                <FontAwesomeIcon icon={stat.icon} />
                            </span>
                        </div>
                        <p className="mv-stat-value">{stat.value}</p>
                        <p className="mv-stat-meta">{stat.meta}</p>
                    </article>
                ))}
            </section>

            <section className="mv-widget-section mv-reveal" style={{ animationDelay: '400ms' } as CSSProperties}>
                <div className="mv-widget-section-head">
                    <p className="mv-widget-section-eyebrow">Widget library</p>
                    <h3 className="mv-widget-section-title">All widgets in your workspace</h3>
                    <p className="mv-widget-section-subtitle">
                        Open any card to edit targeting, content, and visual style in the widget studio.
                    </p>
                </div>
                <WidgetsList />
            </section>
        </div>
    );
}
